import type { QueryClient } from "@tanstack/react-query";
import { meQueryOptions } from "../queries";
import { setSentryUser } from "../sentry";
import { getFirebaseAuth } from "./firebase";
import type { AuthState } from "./AuthProvider";

// The three-way gate for /_authed (spec §2). ensureQueryData = resolved once per
// session, not per navigation; register/sign-out invalidate the ["me"] key.
//
// The router-context `auth` snapshot only updates when AppRouter re-renders, so it can
// be one tick stale relative to Firebase's own state right after signInWithToken()/
// signOut() (I2). Firebase's currentUser is the live source of truth for both
// directions of that race, and it's what auth.getIdToken()/signOut() actually operate
// on regardless of what `auth.user` says — so we consult it as a fallback rather than
// trusting the snapshot alone.
export async function resolveAuthedGate(
  auth: AuthState,
  queryClient: QueryClient,
): Promise<"/login" | "/register" | null> {
  const liveUser = getFirebaseAuth().currentUser;
  if (!liveUser) return "/login"; // covers a genuine signed-out session AND a stale
  // "signedIn" snapshot racing an in-flight signOut().

  // Live Firebase state says signed in, even if the context snapshot hasn't caught up
  // yet after signInWithToken() — proceed with an effective auth carrying the live uid.
  const effectiveAuth: AuthState =
    auth.status === "signedIn" ? auth : { ...auth, status: "signedIn", user: { uid: liveUser.uid } };

  const me = await queryClient.ensureQueryData(meQueryOptions(effectiveAuth));
  setSentryUser(me?.customerId ?? null); // pseudonymous Guid ONLY — never phone/uid
  return me ? null : "/register";
}
